import { User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { UserProfile, Restaurant, UserRole } from './types';
import { getRoleFromJWT, getRoleFromJWTSync, refreshTokenForRoleChanges } from '@/utils/jwt-roles';

// Helper function to get role priority (higher number = higher priority)
export function getRolePriority(role: UserRole | string): number {
  switch(role) {
    case UserRole.SUPER_ADMIN:
      return 4;
    case UserRole.ORG_ADMIN:
      return 3;
    case UserRole.REST_ADMIN:
      return 2;
    case UserRole.USER:
      return 1;
    case UserRole.NEW_SIGN_UP:
    case UserRole.IN_PROGRESS:
      return 0; // Lowest priority as these are onboarding states
    default:
      return 1;
  }
}

// Helper function to convert role strings to enum values
export function convertRoleStringToEnum(role: string): UserRole {
  switch(role) {
    case 'super_admin':
      return UserRole.SUPER_ADMIN;
    case 'org_admin':
      return UserRole.ORG_ADMIN;
    case 'rest_admin':
    case 'admin':
    case 'owner':
    case 'manager':
      return UserRole.REST_ADMIN;
    case 'new_sign_up':
      return UserRole.NEW_SIGN_UP;
    case 'in_progress':
      return UserRole.IN_PROGRESS;
    case 'user':
    default:
      return UserRole.USER;
  }
}

// Fetch or create a user profile from Supabase
export async function fetchOrCreateUserProfile(user: User): Promise<UserProfile | null> {
  console.log(`Fetching profile for user: ${user.id}`);
  
  // First try to get role from JWT for reliability
  const jwtRole = await getRoleFromJWT();
  console.log(`JWT role extraction result: ${jwtRole}`);
  
  try {
    // STRATEGY 1: Try the new view first which combines JWT and DB roles
    const { data: viewProfile, error: viewError } = await supabase
      .from('user_profiles_with_role')
      .select('*')
      .eq('id', user.id)
      .single();
      
    if (!viewError && viewProfile) {
      console.log('Profile fetched from view:', viewProfile.id);
      
      // Convert the database role to enum
      const dbRole = convertRoleStringToEnum(viewProfile.effective_role);
      
      // Compare role priorities and determine which to use
      const dbRolePriority = getRolePriority(dbRole);
      const jwtRolePriority = jwtRole ? getRolePriority(jwtRole) : 0;
      
      let finalRole = dbRole;
      let needTokenRefresh = false;
      
      console.log(`Role comparison - DB role: ${dbRole} (${dbRolePriority}), JWT role: ${jwtRole} (${jwtRolePriority})`);
      
      // If database role has higher priority than JWT role, we need to refresh the token
      if (dbRolePriority > jwtRolePriority) {
        console.log('Database role has higher priority, will trigger token refresh');
        needTokenRefresh = true;
      } 
      // If JWT role has higher priority, use it instead
      else if (jwtRolePriority > dbRolePriority) {
        console.log('JWT role has higher priority, using it instead');
        finalRole = jwtRole as UserRole;
      }
      
      // Create profile with the determined role
      const profile = {
        id: viewProfile.id,
        email: viewProfile.email || '',
        name: viewProfile.name || '',
        role: finalRole,
        organisationId: viewProfile.organisation_id || 'org-001',
        restaurants: await fetchUserRestaurants(viewProfile.id, viewProfile.organisation_id)
      };
      
      // If needed, refresh token to update JWT claims
      if (needTokenRefresh) {
        console.log('Refreshing token to update JWT claims');
        await refreshTokenForRoleChanges();
      }
      
      return profile;
    }
    
    // STRATEGY 2: Try direct profiles table query
    const { data: profile, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    
    if (!error && profile) {
      console.log('Profile fetched from profiles table:', profile.id);
      
      // Convert the database role to enum
      const dbRole = convertRoleStringToEnum(profile.role);
      
      // Compare role priorities and determine which to use
      const dbRolePriority = getRolePriority(dbRole);
      const jwtRolePriority = jwtRole ? getRolePriority(jwtRole) : 0;
      
      let finalRole = dbRole;
      let needTokenRefresh = false;
      
      console.log(`Role comparison - DB role: ${dbRole} (${dbRolePriority}), JWT role: ${jwtRole} (${jwtRolePriority})`);
      
      // If database role has higher priority than JWT role, we need to refresh the token
      if (dbRolePriority > jwtRolePriority) {
        console.log('Database role has higher priority, will trigger token refresh');
        needTokenRefresh = true;
      } 
      // If JWT role has higher priority, use it instead
      else if (jwtRolePriority > dbRolePriority) {
        console.log('JWT role has higher priority, using it instead');
        finalRole = jwtRole as UserRole;
      }
      
      // Create profile with the determined role
      const userProfile = {
        id: profile.id,
        email: profile.email || '',
        name: profile.name || '',
        role: finalRole,
        organisationId: profile.organisation_id || 'org-001',
        restaurants: await fetchUserRestaurants(profile.id, profile.organisation_id)
      };
      
      // If needed, refresh token to update JWT claims
      if (needTokenRefresh) {
        console.log('Refreshing token to update JWT claims');
        await refreshTokenForRoleChanges();
      }
      
      return userProfile;
    }
    
    // STRATEGY 3: If no profile exists, create one
    if (error && error.code === 'PGRST116') {
      console.log('Profile not found, creating new profile');
      
      // Determine the initial role (prioritize JWT role if available)
      const initialRole = jwtRole || UserRole.USER;
      
      const { data: newProfile, error: createError } = await supabase
        .from('profiles')
        .insert([{ 
          id: user.id, 
          email: user.email || '',
          name: user.user_metadata?.name || user.user_metadata?.full_name || '',
          role: initialRole
        }])
        .select()
        .single();
        
      if (!createError && newProfile) {
        console.log('Created new profile for user:', newProfile.id);
        return {
          id: newProfile.id,
          email: newProfile.email || '',
          name: newProfile.name || '',
          role: convertRoleStringToEnum(newProfile.role),
          organisationId: newProfile.organisation_id || 'org-001',
          restaurants: []
        };
      }
    }
    
    // STRATEGY 4: Fallback to JWT-only profile if all else fails
    console.log('All profile fetch strategies failed, using JWT-based fallback');
    return {
      id: user.id,
      email: user.email || '',
      name: user.user_metadata?.name || user.email?.split('@')[0] || 'User',
      role: jwtRole || UserRole.USER,
      organisationId: 'org-001',
      restaurants: []
    };
  } catch (error) {
    console.error('Error in profile fetch process:', error);
    
    // Final fallback using JWT role
    return {
      id: user.id,
      email: user.email || '',
      name: user.user_metadata?.name || user.email?.split('@')[0] || 'User',
      role: jwtRole || UserRole.USER,
      organisationId: 'org-001', 
      restaurants: []
    };
  }
}

// Add organisation and restaurant data to user profile
export async function enrichUserProfile(profile: any): Promise<UserProfile> {
  // Get organisation details if the user belongs to one
  let organisationName = '';
  if (profile.organisation_id) {
    try {
      const { data: organisation, error: orgError } = await supabase
        .from('restaurants_master')
        .select('organisation_name')
        .eq('organisation_id', profile.organisation_id)
        .limit(1)
        .single();
        
      if (!orgError && organisation) {
        organisationName = organisation.organisation_name;
      }
    } catch (error) {
      console.error('Error fetching organisation details:', error);
      // Continue without organisation name
    }
  }
  
  // Get all restaurants the user has access to
  let restaurants: Restaurant[] = [];
  try {
    restaurants = await fetchUserRestaurants(profile.id, profile.organisation_id);
  } catch (error) {
    console.error('Error fetching user restaurants:', error);
    // Continue with empty restaurants array
  }
  
  // Get roles from both JWT and database
  try {
    const jwtRole = await getRoleFromJWT();
    const dbRole = convertRoleStringToEnum(profile.role);
    
    // Compare role priorities
    const dbRolePriority = getRolePriority(dbRole);
    const jwtRolePriority = jwtRole ? getRolePriority(jwtRole) : 0;
    
    let finalRole = dbRole;
    let needTokenRefresh = false;
    
    console.log(`Role comparison in enrichUserProfile - DB role: ${dbRole} (${dbRolePriority}), JWT role: ${jwtRole} (${jwtRolePriority})`);
    
    // If database role has higher priority than JWT role, we need to refresh the token
    if (dbRolePriority > jwtRolePriority) {
      console.log('Database role has higher priority, will trigger token refresh');
      finalRole = dbRole;
      needTokenRefresh = true;
    } 
    // If JWT role has higher priority, use it instead
    else if (jwtRolePriority > dbRolePriority) {
      console.log('JWT role has higher priority, using it instead');
      finalRole = jwtRole as UserRole;
    }
    
    // Create result profile
    const result = {
      id: profile.id,
      email: profile.email,
      name: profile.name || '',
      role: finalRole,
      organisationId: profile.organisation_id || 'org-001',
      organisationName,
      restaurants
    };
    
    // If needed, refresh token to update JWT claims
    if (needTokenRefresh) {
      console.log('Refreshing token to update JWT claims');
      await refreshTokenForRoleChanges();
    }
    
    return result;
  } catch (e) {
    console.error('Error getting role from JWT in enrichUserProfile:', e);
    
    // Try sync method as fallback
    const syncJwtRole = getRoleFromJWTSync();
    const dbRole = convertRoleStringToEnum(profile.role);
    
    // Compare role priorities
    const dbRolePriority = getRolePriority(dbRole);
    const jwtRolePriority = syncJwtRole ? getRolePriority(syncJwtRole) : 0;
    
    let finalRole = dbRole;
    
    // If JWT role has higher priority, use it instead
    if (jwtRolePriority > dbRolePriority) {
      console.log('JWT role has higher priority (sync), using it instead');
      finalRole = syncJwtRole as UserRole;
    } else if (dbRolePriority > jwtRolePriority) {
      console.log('Database role has higher priority (sync), using it. Token refresh needed but will be done later.');
    }
    
    return {
      id: profile.id,
      email: profile.email,
      name: profile.name || '',
      role: finalRole,
      organisationId: profile.organisation_id || 'org-001',
      organisationName,
      restaurants
    };
  }
}

// Fetch all restaurants the user has access to
export async function fetchUserRestaurants(userId: string, organisationId?: string): Promise<Restaurant[]> {
  let restaurants: Restaurant[] = [];
  
  try {
    // Get directly assigned restaurants
    const { data: restaurantAccess, error: accessError } = await supabase
      .from('user_restaurant_access')
      .select('restaurant_id, access_level')
      .eq('user_id', userId);
      
    if (!accessError && restaurantAccess && restaurantAccess.length > 0) {
      // Fetch details for all restaurants the user has access to
      const restaurantIds = restaurantAccess.map(access => access.restaurant_id);
      
      const { data: restaurantDetails, error: restError } = await supabase
        .from('restaurants_master')
        .select('restaurant_id, restaurant_name')
        .in('restaurant_id', restaurantIds);
        
      if (!restError && restaurantDetails) {
        restaurants = restaurantDetails.map(restaurant => {
          const access = restaurantAccess.find(a => a.restaurant_id === restaurant.restaurant_id);
          return {
            id: restaurant.restaurant_id,
            name: restaurant.restaurant_name,
            accessLevel: access?.access_level || 'view'
          };
        });
      }
    }
    
    // If user has organisation access, also add all restaurants from that organisation
    if (organisationId) {
      const { data: orgRestaurants, error: orgRestError } = await supabase
        .from('restaurants_master')
        .select('restaurant_id, restaurant_name')
        .eq('organisation_id', organisationId);
        
      if (!orgRestError && orgRestaurants) {
        // Add organisation restaurants that aren't already in the list
        orgRestaurants.forEach(restaurant => {
          if (!restaurants.some(r => r.id === restaurant.restaurant_id)) {
            restaurants.push({
              id: restaurant.restaurant_id,
              name: restaurant.restaurant_name,
              accessLevel: 'org-member'
            });
          }
        });
      }
    }
  } catch (error) {
    console.error('Error in fetchUserRestaurants:', error);
    // Return whatever restaurants we were able to fetch
  }
  
  return restaurants;
}
